import { FormLabel, Stack, Typography, type StackProps } from '@mui/material';
import { useField } from 'formik';
import { useState, type FC } from 'react';

import "./SelectOptions.scss";
import clsx from 'clsx';
import { FormTextField } from './FormText';

interface SelectOption {
    content: string;
    value: string;
}

interface SelectOptionsProps {
    name: string;
    label: string;
    options: string[] | SelectOption[];
    itemsContainerProps?: StackProps;
    itemProps?: StackProps;
    withElse?: boolean;
    multiple?: boolean;
}

const SelectOptions: FC<SelectOptionsProps> = ({ name, withElse, label, options, itemsContainerProps, itemProps, multiple = false }) => {
    const [field, meta, helpers] = useField<string[] | string>(name);
    const [isElse, setIsElse] = useState(false);

    const handleSelect = (value: string) => {
        setIsElse(false);
        if (multiple) {
            const newValue = (prevValue: string[] | string) => {
                const prevArray = Array.isArray(prevValue) ? prevValue : [prevValue];
                if (prevArray.includes(value)) {
                    return prevArray.filter((v) => v !== value);
                } else {
                    return [...prevArray, value];
                }
            };
            helpers.setValue(newValue(field.value));
        } else {
            if (field.value === value) {
                helpers.setValue('');
            } else {
                helpers.setValue(value);
            }
        }
    };

    const isSelected = (value: string) => {
        if (multiple) {
            return Array.isArray(field.value) && field.value.includes(value);
        } else {
            return field.value === value;
        }
    };

    // eslint-disable-next-line no-console -- error
    if (!options.length) console.error('SelectOptions: options array is empty');

    return (
        <>
            {options.length > 0 && <Stack className='select-options-container field-container' gap="1vh" width="100%">
                {label && (
                    <FormLabel className="field-label" htmlFor={name}>
                        {label}
                    </FormLabel>
                )}
                <Stack className='items-container' width="100%" direction="row" flexWrap="wrap" gap="1rem" {...itemsContainerProps}>
                    {options.map((option) => {
                        const isObject = typeof option === 'object';
                        const value = isObject ? option.value : option;
                        const content = isObject ? option.content : option;

                        return (
                            <Stack
                                onClick={() => handleSelect(value)}
                                className={clsx('item', { chosen: isSelected(value) })}
                                key={value}
                                direction="row"
                                width="max-content"
                                {...itemProps}
                            >
                                {content}
                            </Stack>
                        );
                    })}
                    {withElse && (!isElse
                        ? (
                            <Stack
                                onClick={() => { helpers.setValue(multiple ? [] : ''); setIsElse(true); }}
                                className={clsx('item', { chosen: isElse })}
                                direction="row"
                                width="max-content"
                                {...itemProps}
                            >
                                אחר
                            </Stack>
                        )
                        : <FormTextField
                            helperError={false}
                            inputProps={{ maxLength: 50 }}
                            containerProps={{ sx: { width: "15rem", flexDirection: "row", height: "1rem", alignItems: "center", marginTop: "0.4rem" } }}
                            name={name}
                            label='אחר:'
                        />
                    )}
                </Stack>
                {meta.touched && meta.error && <Typography className='helper-text'>{meta.error}</Typography>}
            </Stack>}
        </>
    );
};

export default SelectOptions;